import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Building2,
  Code2,
  FolderKanban,
  HandCoins,
  Plus,
  Repeat,
  ShieldCheck,
  ShoppingBag,
  UserPlus,
  Users,
} from 'lucide-react';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Select, Skeleton } from '@/components/ui/misc';
import { useSession } from '@/features/auth/use-session';
import { usePermission } from '@/hooks/use-permission';
import { apiGet, apiList } from '@/lib/api';
import { formatDate } from '@/lib/format';
import type { Role, Team, UserRow } from '@/lib/types';
import { ForecastChart, ForecastTable, RevenueChart, RevenueTable } from './charts';
import { compactMoney, exactMoney, percentChange, sumMoney, toNumber, wholeMoney } from './format';
import type { DashboardSummary, ForecastPeek } from './types';
import {
  BarList,
  MiniStat,
  Panel,
  ProjectList,
  SectionHeading,
  SplitMeter,
  StatTile,
  useChartTable,
} from './widgets';

/** How far ahead the dashboard looks. The forecast page itself goes further. */
const FORECAST_MONTHS = 3;

/**
 * The landing screen: how the business is doing.
 *
 * It leads with one number — revenue this year — and its twelve-month shape,
 * then the counts people ask about first (clients, leads, sales, projects),
 * then the detail behind them. Every figure opens the page it came from.
 *
 * Nothing here is shown to somebody who could not open the page behind it:
 * the API leaves a section out rather than send a number the reader may not
 * see, and the layout closes up around whatever is missing.
 */
export function DashboardPage() {
  const { data: user } = useSession();
  const { can } = usePermission();

  const { data: summary, isLoading } = useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: () => apiGet<DashboardSummary>('/dashboard/summary'),
    staleTime: 60_000,
  });

  const { data: forecast } = useQuery({
    queryKey: ['dashboard', 'forecast', FORECAST_MONTHS],
    queryFn: () => apiGet<ForecastPeek>('/forecast', { params: { months: FORECAST_MONTHS } }),
    enabled: can('finance.forecast.view'),
    staleTime: 5 * 60_000,
  });

  const firstName = user?.name.split(' ')[0] ?? '';
  const currency = summary?.currency ?? user?.organization?.baseCurrency ?? 'AED';

  return (
    <div className="space-y-6">
      <Greeting name={firstName} organisation={user?.organization?.name} />

      {summary && summary.unpricedCurrencies.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <span>
            No exchange rate is held for {summary.unpricedCurrencies.join(', ')}, so amounts in{' '}
            {summary.unpricedCurrencies.length === 1 ? 'it are' : 'them are'} counted at face value.
          </span>
        </div>
      )}

      {isLoading || !summary ? (
        <DashboardSkeleton />
      ) : (
        <>
          {summary.revenue && <RevenueHero summary={summary} currency={currency} />}
          <KpiRow summary={summary} currency={currency} />
          <DetailGrid summary={summary} forecast={forecast} currency={currency} />
          {summary.projects && <ProjectsRow projects={summary.projects} currency={currency} />}
        </>
      )}

      <WorkspaceRow />
    </div>
  );
}

// ── Greeting ─────────────────────────────────────────────────────────────────

function Greeting({ name, organisation }: { name: string; organisation?: string }) {
  const hour = new Date().getHours();
  const part = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {formatDate(new Date(), 'EEEE, d MMMM yyyy')}
        </p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">
          {name ? `${part}, ${name}` : part}
        </h1>
        {organisation && <p className="mt-0.5 text-sm text-muted-foreground">{organisation}</p>}
      </div>
      <div className="flex flex-wrap gap-2">
        <Can permission="crm.lead.create">
          <Button variant="outline" size="sm" asChild>
            <Link to="/crm/leads">
              <UserPlus className="h-4 w-4" />
              New lead
            </Link>
          </Button>
        </Can>
        <Can permission="finance.invoice.create">
          <Button size="sm" asChild>
            <Link to="/finance/invoices/new">
              <Plus className="h-4 w-4" />
              New invoice
            </Link>
          </Button>
        </Can>
      </div>
    </div>
  );
}

// ── Hero ─────────────────────────────────────────────────────────────────────

/** The one number the dashboard leads with, and the year it came from. */
function RevenueHero({ summary, currency }: { summary: DashboardSummary; currency: string }) {
  const revenue = summary.revenue!;
  const { mode, toggle } = useChartTable();
  const showCollected = revenue.collectedThisMonth !== null;

  // This month against last month, both from the same twelve buckets.
  const current = revenue.monthly.at(-1);
  const previous = revenue.monthly.at(-2);
  const monthChange =
    current && previous ? percentChange(toNumber(current.invoiced), toNumber(previous.invoiced)) : null;

  return (
    <section className="grid gap-5 rounded-xl border bg-card p-5 shadow-sm lg:grid-cols-[minmax(0,20rem)_1fr] lg:p-6">
      <div className="flex flex-col">
        <Link to="/finance/invoices" className="group" title={exactMoney(revenue.invoicedThisYear, currency)}>
          <p className="text-sm font-medium text-muted-foreground">Revenue this year</p>
          <p className="mt-2 break-words text-4xl font-bold tracking-tight sm:text-5xl">
            {wholeMoney(revenue.invoicedThisYear, currency)}
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">
              {wholeMoney(revenue.invoicedThisMonth, currency)}
            </span>{' '}
            this month
            {monthChange !== null && previous && (
              <>
                {' · '}
                <span className={monthChange >= 0 ? 'text-emerald-700' : 'text-red-700'}>
                  {monthChange >= 0 ? '+' : ''}
                  {monthChange}%
                </span>{' '}
                vs {previous.label.split(' ')[0]}
              </>
            )}
          </p>
        </Link>

        <div className="mt-5 grid grid-cols-3 gap-2 lg:grid-cols-1">
          {showCollected && (
            <MiniStat
              to="/finance/payments"
              label="Collected this month"
              value={compactMoney(revenue.collectedThisMonth, currency)}
              title={exactMoney(revenue.collectedThisMonth, currency)}
            />
          )}
          <MiniStat
            to="/finance/invoices"
            label="Outstanding"
            value={compactMoney(revenue.outstanding, currency)}
            title={exactMoney(revenue.outstanding, currency)}
            hint="Invoiced, not yet paid"
          />
          {summary.sales && (
            <MiniStat
              to="/crm/deals"
              label="Open pipeline"
              value={compactMoney(summary.sales.open.value, currency)}
              title={exactMoney(summary.sales.open.value, currency)}
              hint={`${summary.sales.open.count} ${summary.sales.open.count === 1 ? 'deal' : 'deals'}`}
            />
          )}
        </div>
      </div>

      <div className="min-w-0">
        <div className="mb-2 flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold">Last 12 months</h2>
            <p className="text-xs text-muted-foreground">
              {showCollected ? 'Invoiced against money received' : 'Invoiced each month'}
            </p>
          </div>
          {toggle}
        </div>
        {mode === 'chart' ? (
          <RevenueChart monthly={revenue.monthly} currency={currency} showCollected={showCollected} />
        ) : (
          <RevenueTable monthly={revenue.monthly} currency={currency} showCollected={showCollected} />
        )}
      </div>
    </section>
  );
}

// ── KPI row ──────────────────────────────────────────────────────────────────

function KpiRow({ summary, currency }: { summary: DashboardSummary; currency: string }) {
  const { clients, leads, sales, projects } = summary;
  if (!clients && !leads && !sales && !projects) return null;

  return (
    <section>
      <SectionHeading>At a glance</SectionHeading>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {clients && (
          <StatTile
            to="/crm/companies?type=CLIENT"
            icon={<Building2 className="h-4 w-4" />}
            label="Clients"
            value={clients.total}
            hint="Everyone who has bought"
          />
        )}
        {clients && clients.recurring !== null && (
          <StatTile
            to="/finance/subscriptions"
            icon={<Repeat className="h-4 w-4" />}
            label="Recurring clients"
            value={clients.recurring}
            hint="On a live subscription"
          />
        )}
        {clients && clients.oneTime !== null && (
          <StatTile
            to="/crm/companies?type=CLIENT"
            icon={<ShoppingBag className="h-4 w-4" />}
            label="One-time clients"
            value={clients.oneTime}
            hint="Nothing recurring"
          />
        )}
        {leads && (
          <StatTile
            to="/crm/leads"
            icon={<UserPlus className="h-4 w-4" />}
            label="New leads this month"
            value={leads.thisMonth}
            delta={{
              percent: percentChange(leads.thisMonth, leads.lastMonthToDate),
              against: 'vs last month',
            }}
          />
        )}
        {sales && (
          <StatTile
            to="/crm/deals/list"
            icon={<HandCoins className="h-4 w-4" />}
            label="Sales this year"
            value={compactMoney(sales.wonThisYear.value, currency)}
            title={exactMoney(sales.wonThisYear.value, currency)}
            hint={`${sales.wonThisYear.count} won · ${sales.wonThisMonth.count} this month`}
          />
        )}
        {projects && (
          <StatTile
            to="/delivery/projects"
            icon={<Code2 className="h-4 w-4" />}
            label="In product development"
            value={projects.inDevelopment}
            hint="Being built or about to be"
          />
        )}
      </div>
    </section>
  );
}

// ── Detail grid ──────────────────────────────────────────────────────────────

function DetailGrid({
  summary,
  forecast,
  currency,
}: {
  summary: DashboardSummary;
  forecast?: ForecastPeek;
  currency: string;
}) {
  const { can } = usePermission();
  const pipelines = summary.sales?.pipelines ?? [];
  const showForecast = can('finance.forecast.view');
  const clients = summary.clients;
  const split =
    clients && clients.recurring !== null && clients.oneTime !== null
      ? { recurring: clients.recurring, oneTime: clients.oneTime }
      : null;

  if (pipelines.length === 0 && !showForecast && !split && !summary.leads) return null;

  return (
    <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
      {pipelines.length > 0 && <PipelinePanel pipelines={pipelines} currency={currency} />}

      {showForecast && <ForecastPanel forecast={forecast} currency={currency} />}

      {(split || summary.leads) && (
        <Panel title="Clients & leads" subtitle="Who pays, and who might">
          {split && (
            <SplitMeter
              parts={[
                {
                  key: 'recurring',
                  label: 'Recurring',
                  value: split.recurring,
                  colour: 'var(--chart-1)',
                  to: '/finance/subscriptions',
                },
                {
                  key: 'one-time',
                  label: 'One-time',
                  value: split.oneTime,
                  colour: 'var(--chart-2)',
                  to: '/crm/companies?type=CLIENT',
                },
              ]}
            />
          )}
          {summary.leads && (
            <div className={split ? 'mt-5 border-t pt-4' : ''}>
              <Link
                to="/crm/leads"
                className="grid grid-cols-3 gap-2 rounded-md transition-colors hover:bg-muted"
              >
                <LeadFigure label="All leads" value={summary.leads.total} />
                <LeadFigure label="Still open" value={summary.leads.open} />
                <LeadFigure label="This month" value={summary.leads.thisMonth} />
              </Link>
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}

/**
 * Open deals by stage, one pipeline at a time.
 *
 * A switch rather than a merged chart, because stages of different pipelines
 * are different things. The switch lists each pipeline with its own count, so
 * the deals not on screen are never out of mind.
 */
function PipelinePanel({
  pipelines,
  currency,
}: {
  pipelines: NonNullable<DashboardSummary['sales']>['pipelines'];
  currency: string;
}) {
  // The busiest pipeline is the one worth opening on, the default breaking ties.
  const [selectedId, setSelectedId] = useState(
    () => [...pipelines].sort((a, b) => b.count - a.count || Number(b.isDefault) - Number(a.isDefault))[0].id,
  );
  const selected = pipelines.find((pipeline) => pipeline.id === selectedId) ?? pipelines[0];
  const plural = (count: number) => `${count} ${count === 1 ? 'deal' : 'deals'}`;

  return (
    <Panel
      title="Sales pipeline"
      subtitle={`${plural(selected.count)} open · ${compactMoney(selected.value, currency)}`}
      to="/crm/deals"
      linkLabel="Board"
      actions={
        pipelines.length > 1 ? (
          <Select
            value={selected.id}
            onChange={(event) => setSelectedId(event.target.value)}
            className="h-7 w-auto max-w-[10rem] py-0 text-xs"
            aria-label="Pipeline"
          >
            {pipelines.map((pipeline) => (
              <option key={pipeline.id} value={pipeline.id}>
                {pipeline.name} ({pipeline.count})
              </option>
            ))}
          </Select>
        ) : undefined
      }
    >
      <BarList
        empty="No open deals in this pipeline."
        rows={selected.stages.map((stage) => ({
          key: stage.id,
          label: stage.name,
          value: toNumber(stage.value),
          display: compactMoney(stage.value, currency),
          detail: plural(stage.count),
          title: `${stage.name}: ${exactMoney(stage.value, currency)} across ${plural(stage.count)}`,
        }))}
      />
    </Panel>
  );
}

function LeadFigure({ label, value }: { label: string; value: number }) {
  return (
    <div className="px-2 py-1.5">
      <div className="text-xl font-semibold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function ForecastPanel({ forecast, currency }: { forecast?: ForecastPeek; currency: string }) {
  const { mode, toggle } = useChartTable();
  const last = forecast?.series.at(-1)?.label;

  return (
    <Panel
      title="Cash forecast"
      subtitle={last ? `Through ${last}` : 'The next few months'}
      to="/finance/forecast"
      linkLabel="Open"
      actions={forecast ? toggle : undefined}
    >
      {!forecast ? (
        <Skeleton className="h-56" />
      ) : (
        <>
          <div className="mb-3 grid grid-cols-3 gap-2 text-xs">
            <ForecastFigure
              label="Expected in"
              value={sumMoney(forecast.series.map((bucket) => bucket.inflow))}
              currency={currency}
            />
            <ForecastFigure
              label="Expected out"
              value={sumMoney(forecast.series.map((bucket) => bucket.outflow))}
              currency={currency}
            />
            <ForecastFigure label="Closing cash" value={forecast.closingBalance} currency={currency} />
          </div>
          {forecast.shortfall && (
            <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-red-700">
              <AlertTriangle className="h-3.5 w-3.5" />
              Cash dips below zero in {forecast.shortfall.label}
            </p>
          )}
          {mode === 'chart' ? (
            <ForecastChart forecast={forecast} currency={currency} />
          ) : (
            <ForecastTable forecast={forecast} currency={currency} />
          )}
        </>
      )}
    </Panel>
  );
}

function ForecastFigure({
  label,
  value,
  currency,
}: {
  label: string;
  value: string | null;
  currency: string;
}) {
  return (
    <div className="rounded-lg bg-muted/60 px-2.5 py-2" title={exactMoney(value, currency)}>
      <div className="text-muted-foreground">{label}</div>
      <div className="mt-0.5 truncate text-sm font-semibold">{compactMoney(value, currency)}</div>
    </div>
  );
}

// ── Projects ─────────────────────────────────────────────────────────────────

function ProjectsRow({
  projects,
  currency,
}: {
  projects: NonNullable<DashboardSummary['projects']>;
  currency: string;
}) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Panel
        title={`Ongoing projects · ${projects.ongoing}`}
        subtitle={`${wholeMoney(projects.ongoingValue, currency)} contracted · soonest finish first`}
        to="/delivery/projects"
      >
        <ProjectList projects={projects.ongoingList} when="finish" empty="Nothing under way." />
      </Panel>
      <Panel
        title={`Upcoming projects · ${projects.upcoming}`}
        subtitle="Planned, not started · soonest start first"
        to="/delivery/projects?status=PLANNING"
      >
        <ProjectList projects={projects.upcomingList} when="start" empty="Nothing planned." />
      </Panel>
    </div>
  );
}

// ── Workspace ────────────────────────────────────────────────────────────────

/** People, roles and teams — for whoever runs the workspace, kept small and last. */
function WorkspaceRow() {
  const { can } = usePermission();

  const { data: users } = useQuery({
    queryKey: ['users', { limit: 1 }],
    queryFn: () => apiList<UserRow[]>('/users', { params: { limit: 1 } }),
    enabled: can('admin.user.view'),
  });
  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: () => apiGet<Role[]>('/roles'),
    enabled: can('admin.role.view'),
  });
  const { data: teams } = useQuery({
    queryKey: ['teams'],
    queryFn: () => apiGet<Team[]>('/teams'),
    enabled: can('admin.team.view'),
  });

  const items: { to: string; icon: typeof Users; label: string; value?: number }[] = [];
  if (can('admin.user.view')) items.push({ to: '/admin/users', icon: Users, label: 'People', value: users?.meta.total });
  if (can('admin.role.view')) items.push({ to: '/admin/roles', icon: ShieldCheck, label: 'Roles', value: roles?.length });
  if (can('admin.team.view')) items.push({ to: '/admin/teams', icon: FolderKanban, label: 'Teams', value: teams?.length });
  if (items.length === 0) return null;

  return (
    <section className="flex flex-wrap items-center gap-2 border-t pt-4 text-sm">
      <span className="me-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Workspace
      </span>
      {items.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          className="flex items-center gap-1.5 rounded-full border bg-card px-3 py-1 text-xs transition-colors hover:bg-muted"
        >
          <item.icon className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="font-semibold">{item.value ?? '—'}</span>
          <span className="text-muted-foreground">{item.label}</span>
        </Link>
      ))}
    </section>
  );
}

// ── Loading ──────────────────────────────────────────────────────────────────

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-80 rounded-xl" />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} className="h-28 rounded-xl" />
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-72 rounded-xl" />
        ))}
      </div>
    </div>
  );
}
